import React, { useEffect, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import {
  Bot, MessageSquare, CalendarDays, TrendingUp, Users, AlertTriangle, ThumbsUp, Clock, Coins,
  HelpCircle, Briefcase, Hourglass, CalendarCheck, BadgeCheck, ClipboardCheck, UserX, Sparkles,
  ShieldCheck, Send, Wallet, FileCheck2, Zap,
} from "lucide-react";
import { api, argent } from "./api";
import { Bloc, LienBloc, CarteIcone, Eyebrow, Tableau, Cellule } from "./ui";

/* Les trois sections du tableau de bord ajoutées le 12.10.2026 (« Assistant IA », « Profs »,
   « Attestations »), entre les essais et l'application des familles. Chacune lit sa propre
   route (/stats/assistant, /stats/profs, /stats/attestations) APRÈS les chiffres
   principaux : le tableau de bord paraît tout de suite, et une section en erreur ne cache
   pas les autres. */

const nb = (n) => new Intl.NumberFormat("fr-CH").format(n || 0);
const heures = (x) => `${new Intl.NumberFormat("fr-CH", { maximumFractionDigits: 1 }).format(x || 0)} h`;
const jourFr = (iso) => String(iso || "").slice(0, 10).split("-").reverse().join(".");
const pourcent = (a, b) => (b ? Math.round((100 * a) / b) : 0);
const pluriel = (n, un, plusieurs) => `${nb(n)} ${n > 1 ? plusieurs : un}`;
const JOURS_COURTS = ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."];
const jourSemaine = (iso) => {
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? "" : `${JOURS_COURTS[d.getDay()]} ${jourFr(iso).slice(0, 5)}`;
};

function useChiffres(chemin) {
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState("");
  useEffect(() => {
    let vivant = true;
    api(chemin).then((d) => vivant && setDonnees(d)).catch((e) => vivant && setErreur(e.message || "erreur"));
    return () => { vivant = false; };
  }, [chemin]);
  return [donnees, erreur];
}

/* En attendant les chiffres : la forme des cartes, sans texte qui saute. */
const Attente = ({ erreur, testid }) => (erreur ? (
  <p className="text-sm" style={{ color: "#B91C1C" }} data-testid={testid}>
    {erreur === "session" ? "Session expirée, reconnecte-toi." : `Ces chiffres ne sont pas disponibles pour le moment (${erreur}).`}
  </p>
) : (
  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-pulse" data-testid={testid}>
    {[0, 1, 2, 3].map((k) => <div key={k} className="h-28 rounded-2xl" style={{ background: "#F1F5F9" }} />)}
  </div>
));

/* Un encadré blanc à l'intérieur d'une section : un titre discret, puis son contenu. */
export const Encart = ({ titre, aide, droite, children, testid, className = "" }) => (
  <div className={`rounded-2xl border p-5 min-w-0 ${className}`} style={{ borderColor: "var(--border)", background: "#fff" }} data-testid={testid}>
    <div className="flex items-start gap-3">
      <div className="min-w-0">
        <Eyebrow>{titre}</Eyebrow>
        {aide && <p className="text-xs text-[var(--muted)] mt-1 leading-snug">{aide}</p>}
      </div>
      {droite && <div className="ml-auto shrink-0">{droite}</div>}
    </div>
    <div className="mt-4">{children}</div>
  </div>
);

/* Une barre découpée en parts (chaque part a sa couleur), et la légende chiffrée dessous. */
export const Repartition = ({ parts, testid, vide = "Rien pour l'instant.", colonnes = false }) => {
  const total = parts.reduce((n, p) => n + (p.valeur || 0), 0);
  const visibles = parts.filter((p) => p.valeur > 0 || p.toujours);
  return (
    <div data-testid={testid}>
      <div className="flex h-3 rounded-full overflow-hidden" style={{ background: "#E2E8F0" }}>
        {total > 0 && parts.filter((p) => p.valeur > 0).map((p) => (
          <div key={p.cle} title={`${p.label} : ${p.valeur}`} style={{ width: `${(100 * p.valeur) / total}%`, background: p.couleur }} />
        ))}
      </div>
      {total === 0 ? (
        <p className="text-sm text-[var(--muted)] mt-3">{vide}</p>
      ) : (
        <ul className={colonnes ? "mt-3 grid sm:grid-cols-2 gap-x-8 gap-y-2" : "mt-3 space-y-2"}>
          {visibles.map((p) => (
            <li key={p.cle} className="flex items-start gap-2.5 min-w-0" data-testid={testid ? `${testid}-${p.cle}` : undefined}>
              <span className="w-2.5 h-2.5 rounded-full shrink-0 mt-1.5" style={{ background: p.couleur }} />
              <span className="min-w-0">
                <span className="text-sm">{p.label}</span>
                {p.detail && <span className="block text-[11px] text-[var(--muted)] leading-snug">{p.detail}</span>}
              </span>
              <span className="ml-auto text-sm font-semibold tabular-nums">{nb(p.valeur)}</span>
              <span className="text-[11px] text-[var(--muted)] w-9 text-right tabular-nums mt-0.5">{pourcent(p.valeur, total)} %</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* Une ligne « à surveiller » : pastille, explication, chiffre à droite. Cliquable si elle
   mène quelque part. */
const COULEURS_SIGNAL = {
  rouge: { pastille: "#FEE2E2", icone: "#B91C1C", pilule: "#FEF2F2", bord: "#FECACA", texte: "#B91C1C" },
  ambre: { pastille: "#FEF3C7", icone: "#B45309", pilule: "#FFFBEB", bord: "#FDE68A", texte: "#B45309" },
  vert: { pastille: "#DCFCE7", icone: "#15803D", pilule: "#F0FDF4", bord: "#BBF7D0", texte: "#15803D" },
  neutre: { pastille: "#EEF2F6", icone: "#475569", pilule: "#F8FAFC", bord: "#E2E8F0", texte: "#0F1B2D" },
};
const Signal = ({ Icon, label, detail, valeur, ton = "neutre", onClick, testid }) => {
  const c = COULEURS_SIGNAL[ton] || COULEURS_SIGNAL.neutre;
  const Balise = onClick ? "button" : "div";
  return (
    <Balise onClick={onClick} data-testid={testid}
      className={`w-full text-left flex items-center gap-3 py-2.5 border-b last:border-b-0 ${onClick ? "group" : ""}`}
      style={{ borderColor: "var(--border-soft)" }}>
      <span className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: c.pastille, color: c.icone }}>
        <Icon size={15} />
      </span>
      <span className="min-w-0">
        <span className={`block text-sm font-medium ${onClick ? "group-hover:underline" : ""}`}>{label}</span>
        {detail && <span className="block text-[11px] text-[var(--muted)] leading-snug">{detail}</span>}
      </span>
      <span className="ml-auto text-sm font-semibold px-2.5 py-1 rounded-full whitespace-nowrap tabular-nums"
        style={{ background: c.pilule, color: c.texte, border: `1px solid ${c.bord}` }}>
        {valeur}
      </span>
    </Balise>
  );
};

/* Une progression où « plus » est mieux (envoyées, appels faits) : une seule couleur, celle
   de la section. La Barre du remplissage, elle, vire au rouge au-delà de 85 %. */
const Progres = ({ pourcent: p, couleur, testid }) => (
  <div className="flex items-center gap-2" data-testid={testid}>
    <div className="h-1.5 rounded-full flex-1 min-w-[60px]" style={{ background: "#E2E8F0" }}>
      <div className="h-1.5 rounded-full" style={{ width: `${Math.min(p, 100)}%`, background: couleur }} />
    </div>
    <span className="text-xs tabular-nums w-9 text-right">{p} %</span>
  </div>
);

const infobulle = { contentStyle: { borderRadius: 12, border: "1px solid #E5E7EB", fontSize: 12 } };

// ── Assistant IA ────────────────────────────────────────────────────────────

const COULEUR_SITE = "#4338CA";
const COULEUR_INSTAGRAM = "#DB2777";

export function SectionAssistant({ onOuvrir }) {
  const [d, erreur] = useChiffres("/stats/assistant");
  return (
    <Bloc id="tdb-assistant" titre="Assistant IA" Icon={Bot} ton="assistant" testid="section-assistant"
      aide="Les questions posées à l'assistant sur le site et sur Instagram. Les réactions aux reels et aux stories, merci et emojis ne comptent pas (voir « Instagram ce mois »)."
      action={<LienBloc ton="assistant" onClick={() => onOuvrir?.("assistant")} testid="assistant-ouvrir">Ouvrir l'assistant</LienBloc>}>
      {!d ? <Attente erreur={erreur} testid="assistant-attente" /> : <ChiffresAssistant d={d} onOuvrir={onOuvrir} />}
    </Bloc>
  );
}

function ChiffresAssistant({ d, onOuvrir }) {
  const m = d.messages;
  const ig = d.instagram_mois;
  const totalIg = Object.values(ig).reduce((n, v) => n + v, 0);
  const serie = d.serie.map((j) => ({ ...j, etiquette: jourFr(j.jour).slice(0, 5) }));
  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <CarteIcone titre="Messages pour l'assistant" valeur={nb(m.total)} Icon={MessageSquare} teinte="indigo" testid="assistant-total"
          detail={`${d.depuis ? `depuis le ${jourFr(d.depuis)}` : "aucun message pour l'instant"} · site ${nb(m.par_canal_total.site)} · Instagram ${nb(m.par_canal_total.instagram)}`} />
        <CarteIcone titre="Ce mois-ci" valeur={nb(m.mois)} Icon={CalendarDays} teinte="bleu" testid="assistant-mois"
          detail={`${d.mois_label} · site ${nb(m.par_canal_mois.site)} · Instagram ${nb(m.par_canal_mois.instagram)} · le mois dernier : ${nb(m.mois_dernier)}`} />
        <CarteIcone titre="Cette semaine" valeur={nb(m.semaine)} Icon={TrendingUp} teinte="vert" testid="assistant-semaine"
          detail={`depuis lundi · la semaine dernière : ${nb(m.semaine_derniere)}`} />
        <CarteIcone titre="Personnes ce mois" valeur={nb(d.personnes.mois)} Icon={Users} teinte="rose" testid="assistant-personnes"
          detail={`${pluriel(d.conversations_mois.site, "conversation", "conversations")} sur le site · ${pluriel(d.conversations_mois.instagram, "personne", "personnes")} sur Instagram`} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Encart titre="Les 30 derniers jours" aide="Messages pour l'assistant chaque jour, site et Instagram." className="lg:col-span-2" testid="assistant-graphe">
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={serie} margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#EEF2F6" />
                <XAxis dataKey="etiquette" tick={{ fontSize: 10, fill: "#64748B" }} interval={4} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#64748B" }} tickLine={false} axisLine={false} />
                <Tooltip {...infobulle} cursor={{ fill: "rgba(67,56,202,0.06)" }} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="site" name="Site" stackId="m" fill={COULEUR_SITE} radius={[0, 0, 0, 0]} />
                <Bar dataKey="instagram" name="Instagram" stackId="m" fill={COULEUR_INSTAGRAM} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Encart>

        <Encart titre="D'où viennent les messages" aide={`En ${d.mois_label.split(" ")[0]}.`} testid="assistant-provenance">
          <Repartition testid="assistant-canaux" vide="Aucun message ce mois-ci." parts={[
            { cle: "site", label: "Site internet", couleur: COULEUR_SITE, valeur: m.par_canal_mois.site, toujours: true },
            { cle: "instagram", label: "Instagram", couleur: COULEUR_INSTAGRAM, valeur: m.par_canal_mois.instagram, toujours: true },
          ]} />
          {d.pages.length > 0 && (
            <div className="mt-5">
              <div className="text-[11px] font-semibold text-[var(--muted)] mb-1.5">Pages du site d'où l'on écrit</div>
              <ul className="space-y-1" data-testid="assistant-pages">
                {d.pages.map((p) => (
                  <li key={p.nom} className="flex items-center gap-2 text-sm">
                    <span className="truncate">{p.nom}</span>
                    <span className="ml-auto text-xs font-semibold tabular-nums text-[var(--muted)]">{nb(p.conversations)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(d.lieux?.pays || []).length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5" data-testid="assistant-pays">
              {d.lieux.pays.slice(0, 4).map((p) => (
                <span key={p.nom} className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#EEF2FF", color: "#3730A3" }}>
                  {p.nom} · {nb(p.conversations)}
                </span>
              ))}
            </div>
          )}
        </Encart>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Encart titre="Instagram ce mois : tous les messages reçus" testid="assistant-instagram"
          aide="Chaque message dans une seule case. Seuls « Répondu » et « Passé à l'équipe » comptent dans les chiffres du haut ; ne pas répondre à un merci, un emoji ou une story est voulu.">
          <Repartition testid="assistant-instagram-parts" vide="Aucun message Instagram ce mois-ci." parts={[
            { cle: "repondu", label: "Répondu par l'assistant", couleur: "#16A34A", valeur: ig.repondu },
            { cle: "equipe_demandee", label: "Passé à l'équipe", detail: "la personne voulait un humain ou n'était pas contente", couleur: "#EA580C", valeur: ig.equipe_demandee },
            { cle: "laisse_equipe", label: "Laissé à l'équipe", detail: "question personnelle, dossier, message flou, ou l'équipe venait de répondre", couleur: "#3B82F6", valeur: ig.laisse_equipe },
            { cle: "pas_besoin", label: "Pas besoin de réponse", detail: "merci, emoji, réaction à une story", couleur: "#94A3B8", valeur: ig.pas_besoin },
            { cle: "propose", label: "Proposé sans être envoyé", detail: "mode observation", couleur: "#A78BFA", valeur: ig.propose },
            { cle: "probleme", label: "Pas de réponse à cause d'un problème", detail: "plafond du jour atteint, erreur", couleur: "#DC2626", valeur: ig.probleme },
          ]} />
          {totalIg > 0 && <p className="text-[11px] text-[var(--muted)] mt-3">{pluriel(totalIg, "message reçu", "messages reçus")} sur Instagram ce mois-ci.</p>}
        </Encart>

        <Encart titre="À surveiller" testid="assistant-surveiller">
          <Signal Icon={AlertTriangle} testid="assistant-attend-equipe"
            label="Attendent une réponse de l'équipe" detail="Instagram : la personne a demandé un humain — réponds depuis Instagram"
            valeur={nb(d.attend_equipe)} ton={d.attend_equipe ? "rouge" : "vert"}
            onClick={d.attend_equipe ? () => onOuvrir?.("notifications") : undefined} />
          <Signal Icon={HelpCircle} testid="assistant-sans-reponse"
            label="Questions sans réponse ce mois" detail="L'assistant n'avait pas l'information : à ajouter à ses consignes (Assistant IA → Statistiques)"
            valeur={nb(d.sans_reponse.mois)} ton={d.sans_reponse.mois ? "ambre" : "vert"}
            onClick={() => onOuvrir?.("assistant")} />
          <Signal Icon={ThumbsUp} testid="assistant-avis"
            label="Avis des visiteurs ce mois" detail="Pouces levés ou baissés sous les réponses, sur le site"
            valeur={`👍 ${nb(d.avis_mois.haut)} · 👎 ${nb(d.avis_mois.bas)}`} ton={d.avis_mois.bas > d.avis_mois.haut ? "ambre" : "neutre"} />
          <Signal Icon={Coins} testid="assistant-cout"
            label="Coût de l'assistant ce mois" detail="Facturé par Anthropic, en dollars (site et Instagram)"
            valeur={`≈ ${Number(d.cout_mois || 0).toFixed(2)} $`} />
          {d.heure_de_pointe !== null && d.heure_de_pointe !== undefined && (
            <Signal Icon={Clock} testid="assistant-heure"
              label="Moment où l'on écrit le plus" detail="Heure suisse, sur les messages du mois"
              valeur={`vers ${d.heure_de_pointe} h`} />
          )}
        </Encart>
      </div>
    </>
  );
}

// ── Profs ───────────────────────────────────────────────────────────────────

const STATUTS = {
  brouillon: { label: "pas envoyé", fond: "#F1F5F9", texte: "#475569", bord: "#E2E8F0" },
  confirme: { label: "à valider", fond: "#FFFBEB", texte: "#B45309", bord: "#FDE68A" },
  valide: { label: "validé", fond: "#F0FDF4", texte: "#15803D", bord: "#BBF7D0" },
};
const Statut = ({ statut, vide }) => {
  if (vide) return <span className="text-xs text-[var(--muted)]">—</span>;
  const s = STATUTS[statut] || STATUTS.brouillon;
  return (
    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap"
      style={{ background: s.fond, color: s.texte, borderColor: s.bord }}>{s.label}</span>
  );
};
const moisCourt = (label) => String(label || "").split(" ")[0];
// « Mois d'octobre », « Mois de novembre ».
const deMois = (m) => (/^[aeiouyéèêh]/i.test(m) ? `d'${m}` : `de ${m}`);
/* « septembre : Lydia, Sarah · octobre : Tom » — les décomptes en attente, mois par mois. */
const parMois = (liste) => {
  const groupes = {};
  liste.forEach((x) => { (groupes[x.mois_label] = groupes[x.mois_label] || []).push(x.prof); });
  return Object.entries(groupes).map(([m, noms]) => `${moisCourt(m)} : ${noms.join(", ")}`).join(" · ");
};

export function SectionProfs({ onOuvrir }) {
  const [d, erreur] = useChiffres("/stats/profs");
  return (
    <Bloc id="tdb-profs" titre="Profs" Icon={Briefcase} ton="profs" testid="section-profs"
      aide="Heures et décomptes des profs payés (brut). Le prof confirme ses heures, l'école valide le décompte."
      action={<LienBloc ton="profs" onClick={() => onOuvrir?.("profs")} testid="profs-ouvrir">Ouvrir les profs</LienBloc>}>
      {!d ? <Attente erreur={erreur} testid="profs-attente" /> : <ChiffresProfs d={d} onOuvrir={onOuvrir} />}
    </Bloc>
  );
}

/* Les quatre chiffres clés des profs : au tableau de bord et en tête du menu Profs (Jen,
   31.10.2026 : « l'onglet prof aussi doit avoir les stats clés »). */
export function CartesProfs({ onOuvrir }) {
  const [d, erreur] = useChiffres("/stats/profs");
  if (erreur) return null;
  if (!d) return <Attente testid="profs-chiffres-attente" />;
  return <GrilleProfs d={d} onOuvrir={onOuvrir} />;
}

function GrilleProfs({ d, onOuvrir }) {
  const pt = d.precedent_totaux;
  const st = pt.statuts;
  const statutsPrecedent = [
    st.valide && `${nb(st.valide)} validé${st.valide > 1 ? "s" : ""}`,
    st.confirme && `${nb(st.confirme)} à valider`,
    st.brouillon && `${nb(st.brouillon)} pas encore envoyé${st.brouillon > 1 ? "s" : ""} par le prof`,
  ].filter(Boolean).join(" · ");
  return (
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="profs-chiffres">
        <CarteIcone titre={`Heures prévues en ${moisCourt(d.mois_label)}`} valeur={heures(d.courant.heures)} Icon={Clock} teinte="orange" testid="profs-mois"
          detail={`dont ${heures(d.courant.donnees)} déjà données · ${argent(d.courant.total)} brut prévu · ${pluriel(d.nb_profs, "prof", "profs")}`} />
        <CarteIcone titre={`Mois ${deMois(moisCourt(d.precedent_label))}`} Icon={CalendarCheck} teinte="bleu" testid="profs-precedent"
          valeur={d.precedent_dans_la_saison ? heures(pt.heures) : "—"}
          detail={d.precedent_dans_la_saison
            ? `${argent(pt.total)} brut${statutsPrecedent ? ` · ${statutsPrecedent}` : ""}`
            : d.precedent_hors_app ? "fait à la main, avant l'application" : "avant le début de la saison"} />
        <CarteIcone titre="Saison : heures validées" valeur={heures(d.saison_validee.heures)} Icon={BadgeCheck} teinte="vert" testid="profs-saison"
          detail={`${argent(d.saison_validee.total)} brut · ${pluriel(d.saison_validee.decomptes, "décompte validé", "décomptes validés")}`} />
        <CarteIcone titre="Décomptes à valider" valeur={nb(d.a_valider.length)} Icon={Hourglass} testid="profs-a-valider"
          teinte={d.a_valider.length ? "ambre" : "neutre"} onClick={() => onOuvrir?.("profs")}
          detail={d.a_valider.length
            ? `envoyés par le prof · ${parMois(d.a_valider)}`
            : "tout ce que les profs ont envoyé est validé"} />
      </div>
  );
}

function ChiffresProfs({ d, onOuvrir }) {
  const a = d.appels;
  return (
    <>
      <GrilleProfs d={d} onOuvrir={onOuvrir} />

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 min-w-0 space-y-2" data-testid="profs-par-prof">
          <div className="px-1">
            <Eyebrow>Par prof</Eyebrow>
            <p className="text-xs text-[var(--muted)] mt-1">Heures comptées (lignes retirées exclues). Saison : décomptes validés seulement.</p>
          </div>
          <div>
            <Tableau
              testid="tableau-profs"
              entetes={["Prof", { label: moisCourt(d.mois_label), droite: true }, "",
                { label: moisCourt(d.precedent_label), droite: true }, "", { label: "Saison validée", droite: true }]}
              lignes={d.par_prof}
              vide="Aucun prof payé pour l'instant."
              rendu={(p) => (
                <>
                  <Cellule fort>{p.prenom}</Cellule>
                  <Cellule droite>
                    <span className="font-semibold">{heures(p.mois.heures)}</span>
                    {p.mois.donnees > 0 && p.mois.donnees < p.mois.heures && (
                      <span className="block text-[11px] text-[var(--muted)]">dont {heures(p.mois.donnees)} données</span>
                    )}
                  </Cellule>
                  <Cellule><Statut statut={p.mois.statut} vide={!p.mois.heures && p.mois.statut === "brouillon"} /></Cellule>
                  <Cellule droite>{p.precedent ? heures(p.precedent.heures) : "—"}</Cellule>
                  <Cellule><Statut statut={p.precedent?.statut} vide={!p.precedent || (!p.precedent.heures && p.precedent.statut !== "valide")} /></Cellule>
                  <Cellule droite>
                    <span className="font-semibold">{heures(p.saison.heures)}</span>
                    {p.saison.total > 0 && <span className="block text-[11px] text-[var(--muted)]">{argent(p.saison.total)}</span>}
                  </Cellule>
                </>
              )}
            />
          </div>
        </div>

        <div className="space-y-4 min-w-0">
          <Encart titre={`L'appel en ${moisCourt(d.mois_label)}`} testid="profs-appels"
            aide="Séances déjà passées : l'appel est fait dans l'app du prof ou en mode école.">
            {a.seances === 0 ? (
              <p className="text-sm text-[var(--muted)]">Aucune séance passée ce mois-ci.</p>
            ) : (
              <>
                <div className="flex items-end gap-2">
                  <span className="font-display text-3xl leading-none">{pourcent(a.faits, a.seances)} %</span>
                  <span className="text-xs text-[var(--muted)] mb-0.5">{nb(a.faits)} séances sur {nb(a.seances)}</span>
                </div>
                <div className="mt-2"><Progres pourcent={pourcent(a.faits, a.seances)} couleur="#EA580C" testid="profs-appels-barre" /></div>
                {a.manquants.length > 0 && (
                  <ul className="mt-3 space-y-1" data-testid="profs-appels-manquants">
                    <li className="text-[11px] font-semibold text-[var(--muted)]">Sans appel (les plus récentes)</li>
                    {a.manquants.map((s) => (
                      <li key={`${s.cours_id}-${s.date}`} className="text-xs flex gap-2">
                        <span className="tabular-nums text-[var(--muted)] w-16 shrink-0">{jourSemaine(s.date)}</span>
                        <span className="truncate">{s.cours}</span>
                        <span className="ml-auto text-[var(--muted)] shrink-0">{s.prof}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </Encart>
          <Encart titre={`Absences et cours privés en ${moisCourt(d.mois_label)}`} testid="profs-absences">
            <Signal Icon={UserX} label="Absences annoncées" testid="profs-absences-total"
              detail={Object.entries(d.absences.par_type).map(([k, v]) => `${k} ${v}`).join(" · ") || "aucune ce mois-ci"}
              valeur={nb(d.absences.total)} />
            {(d.absences.liste || []).length > 0 && (
              <ul className="text-xs space-y-1 py-2 border-b" style={{ borderColor: "var(--border-soft)" }} data-testid="profs-absences-liste">
                {d.absences.liste.map((x, i) => (
                  <li key={i} className="flex flex-wrap gap-x-2">
                    <span className="font-semibold">{x.prof}</span>
                    <span className="text-[var(--muted)]">{x.du === x.au ? jourSemaine(x.du) : `${jourSemaine(x.du)} → ${jourSemaine(x.au)}`} · {x.type}</span>
                    {/* Un remplaçant externe proposé par le prof : « à confirmer » tant que l'école
                        n'a pas touché « Remplaçant confirmé » (01.11.2026). */}
                    <span className="ml-auto" style={{ color: x.remplacant && !x.a_confirmer ? "#15803D" : "#B45309" }}>
                      {x.remplacant ? `remplaçant : ${x.remplacant}${x.a_confirmer ? " (à confirmer)" : ""}` : "sans remplaçant"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <Signal Icon={AlertTriangle} label="Sans remplaçant désigné" testid="profs-sans-remplacant"
              detail="Profs → Absences et remplacements" valeur={nb(d.absences.sans_remplacant)}
              ton={d.absences.sans_remplacant ? "ambre" : "vert"}
              onClick={d.absences.sans_remplacant ? () => onOuvrir?.("profs") : undefined} />
            <Signal Icon={Sparkles} label="Cours privés comptés" testid="profs-cours-prives"
              detail={`${argent(d.cours_prives.montant)} au tarif du site`} valeur={nb(d.cours_prives.nombre)} />
          </Encart>
        </div>
      </div>
    </>
  );
}

// ── Attestations ────────────────────────────────────────────────────────────

/* Les cours privés (Jen, 31.10.2026 : « tableau de bord, statistiques : les cours privés on
   doit les inclure »). La route répond 404 là où le chantier est fermé : rien ne s'affiche. */
export function SectionPrives({ onOuvrir }) {
  const [d, erreur] = useChiffres("/stats/prives");
  if (erreur) return null;
  return (
    <Bloc id="tdb-prives" titre="Cours privés" Icon={Sparkles} ton="application" testid="section-prives"
      aide="Réservés sur le site, payés sur place. Saison : depuis le 1er août."
      action={<LienBloc ton="application" onClick={() => onOuvrir?.("cours-prives")} testid="prives-ouvrir">Ouvrir les cours privés</LienBloc>}>
      {/* Un chiffre clé ouvre le menu Cours privés sur SA liste (Jen, 01.11.2026). */}
      {!d ? <Attente testid="prives-attente" /> : <ChiffresPrives d={d} onVoir={(vue) => window.dispatchEvent(
        new CustomEvent("console-aller", { detail: { onglet: "cours-prives", vue } }))} />}
    </Bloc>
  );
}

/* `onVoir(vue)` : toucher « À venir », le mois ou « Annulés » mène à la liste correspondante
   (Jen, 01.11.2026 : « quand je clique sur la bulle À venir, ça doit m'amener vers la liste
   à venir »). La saison n'a pas de liste à elle : elle reste un chiffre. */
export function ChiffresPrives({ d, onVoir }) {
  const voir = (vue) => (onVoir ? () => onVoir(vue) : undefined);
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="prives-chiffres">
        <CarteIcone titre="À venir" valeur={nb(d.a_venir)} Icon={CalendarDays} teinte="rose" testid="prives-a-venir"
          detail={`dont ${pluriel(d.semaine, "cette semaine", "cette semaine")}`} onClick={voir("a_venir")} />
        <CarteIcone titre={`En ${d.mois_label.split(" ")[0]}`} valeur={nb(d.mois)} Icon={CalendarCheck} teinte="violet" testid="prives-mois"
          detail={`${argent(d.mois_montant)} prévus`} onClick={voir("mois")} />
        <CarteIcone titre="Saison" valeur={nb(d.saison)} Icon={TrendingUp} teinte="vert" testid="prives-saison"
          detail={`${argent(d.saison_montant)} · ${heures(d.saison_heures)} de cours`} />
        {/* Les absents sans annulation (facturés, 01.11.2026) se comptent avec les annulés. « 24 h » :
            le seuil d'annulation gratuite des Réglages › Cours privés (02.11.2026). */}
        <CarteIcone titre="Annulés" valeur={nb((d.annulees || 0) + (d.absents || 0))} Icon={UserX} teinte={d.tardives || d.absents ? "ambre" : "neutre"} testid="prives-annules"
          detail={d.tardives || d.absents
            ? `dont ${[d.tardives ? `${d.tardives} à moins de ${d.annulation_heures || 24} h` : "", d.absents ? `${d.absents} absent·e${d.absents > 1 ? "s" : ""}` : ""].filter(Boolean).join(" · ")} · ${argent(d.dus ?? d.tardives_dues)} dus`
            : `aucun à moins de ${d.annulation_heures || 24} h`}
          onClick={voir("annulees")} />
      </div>
      {(d.par_prof || []).length > 0 && (
        <div className="grid sm:grid-cols-3 gap-3" data-testid="prives-par-prof">
          {d.par_prof.map((p) => (
            <div key={p.prof_id} className="rounded-2xl border bg-white px-4 py-3 flex items-center gap-3" style={{ borderColor: "var(--border)" }}>
              <span className="w-3 h-3 rounded-full shrink-0" style={{ background: p.couleur || "#94A3B8" }} />
              <span className="font-semibold">{p.prenom}</span>
              <span className="ml-auto text-xs text-[var(--muted)] text-right">{pluriel(p.a_venir, "à venir", "à venir")} · {pluriel(p.saison, "cours", "cours")} cette saison ({heures(p.heures_saison)})</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SectionAttestations({ onOuvrir }) {
  const [d, erreur] = useChiffres("/stats/attestations");
  return (
    <Bloc id="tdb-attestations" titre="Attestations" Icon={ShieldCheck} ton="attestations" testid="section-attestations"
      aide={`${d?.etiquette ? `${d.etiquette} — ` : ""}une attestation par élève, qui part quand la facture de sa famille est payée.`}
      action={<LienBloc ton="attestations" onClick={() => onOuvrir?.("attestations")} testid="attestations-ouvrir">Ouvrir les attestations</LienBloc>}>
      {!d ? <Attente erreur={erreur} testid="attestations-attente" /> : <ChiffresAttestations d={d} />}
    </Bloc>
  );
}

function ChiffresAttestations({ d }) {
  const r = d.repartition || {};
  const auto = d.auto || {};
  const autoActif = auto.actif && (!auto.des || auto.des <= new Date().toISOString().slice(0, 10));
  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <CarteIcone titre="Envoyées" valeur={`${nb(d.envoyees)} / ${nb(d.total)}`} Icon={Send} teinte="sarcelle" testid="attestations-envoyees"
          detail={`${argent(d.montant_atteste)} attestés · ${pluriel(d.envoyees_saison, "envoyée", "envoyées")} sur toute la saison`}
          enfants={<div className="mt-2"><Progres pourcent={pourcent(d.envoyees, d.total)} couleur="#0D9488" testid="attestations-barre" /></div>} />
        <CarteIcone titre="Prêtes à envoyer" valeur={nb(d.pretes)} Icon={FileCheck2} teinte={d.pretes ? "bleu" : "neutre"} testid="attestations-pretes"
          detail={autoActif ? "elles partent toutes seules (envoi automatique à 6h15)" : "facture payée : un clic dans Attestations"} />
        <CarteIcone titre="Sans n° AVS" valeur={nb(d.bloquees)} Icon={AlertTriangle} teinte={d.bloquees ? "ambre" : "neutre"} testid="attestations-sans-avs"
          detail={d.bloquees ? `dont ${pluriel(d.bloquees_enfants, "enfant", "enfants")} · le numéro est réclamé aux familles` : "tous les élèves ont leur numéro"} />
        <CarteIcone titre="Attendent le paiement" valeur={nb(d.attend_paiement)} Icon={Wallet} teinte="neutre" testid="attestations-paiement"
          detail="elles partiront quand la facture sera payée" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Encart titre="Où en est chaque élève" className="lg:col-span-2" testid="attestations-repartition"
          aide={`${pluriel(d.total, "élève concerné", "élèves concernés")} ce semestre · ${nb(d.enfants)} enfant${d.enfants > 1 ? "s" : ""}, ${nb(d.adultes)} adulte${d.adultes > 1 ? "s" : ""}.`}>
          <Repartition testid="attestations-parts" vide="Aucune attestation ce semestre pour l'instant." parts={[
            { cle: "envoyee", label: "Envoyée, à jour", couleur: "#0D9488", valeur: r.envoyee || 0 },
            { cle: "a_refaire", label: "Envoyée, à refaire", detail: "la facture a été corrigée et renvoyée depuis", couleur: "#DC2626", valeur: r.a_refaire || 0 },
            { cle: "prete", label: "Prête à envoyer", couleur: "#2563EB", valeur: r.prete || 0 },
            { cle: "attend_paiement", label: "Attend le paiement de la facture", couleur: "#94A3B8", valeur: r.attend_paiement || 0 },
            { cle: "sans_avs", label: "Bloquée : pas de n° AVS", couleur: "#F59E0B", valeur: r.sans_avs || 0 },
            { cle: "facture_en_correction", label: "Facture en cours de correction", couleur: "#A78BFA", valeur: r.facture_en_correction || 0 },
            { cle: "a_zero", label: "À 0 CHF : rien à attester", couleur: "#CBD5E1", valeur: r.a_zero || 0 },
          ]} />
        </Encart>
        <Encart titre="Envoi automatique" testid="attestations-auto">
          <Signal Icon={Zap} testid="attestations-auto-etat"
            label={autoActif ? "Allumé" : auto.actif ? `Prévu dès le ${jourFr(auto.des)}` : "Éteint"}
            detail={autoActif ? "Chaque matin à 6h15, les attestations prêtes partent seules."
              : "Les attestations partent au clic (Réglages → Attestations et e-mails)."}
            valeur={autoActif ? "oui" : "non"} ton={autoActif ? "vert" : "neutre"} />
          {auto.dernier_passage && (
            <Signal Icon={ClipboardCheck} testid="attestations-auto-passage"
              label="Dernier passage" detail={`le ${jourFr(auto.dernier_passage)}`}
              valeur={pluriel(auto.dernier_envoyees, "envoyée", "envoyées")} />
          )}
          <Signal Icon={HelpCircle} testid="attestations-a-zero"
            label="À 0 CHF" detail="Désinscription au 1er jour ou forfait entièrement remisé : elles ne partent pas."
            valeur={nb(d.a_zero)} />
        </Encart>
      </div>
    </>
  );
}
